/**
 * Analytics Service
 * Singleton service callable from anywhere (React, sagas, utils, etc.)
 */

import type { AnalyticsAdapter } from '../adapters'
import type {
  AnalyticsConfig,
  PageViewEvent,
  TrackEvent,
  IdentifyEvent,
  ErrorEvent,
  PerformanceEvent,
  TrackEventPayload,
  ErrorPayload,
  PerformancePayload,
  UserTraits,
} from '../../domain/types'
import {
  sanitizePii,
  getOrCreateSessionId,
  clearSession,
  getDeviceType,
  isRtl,
  getLocale,
  generateErrorFingerprint,
} from '../../domain/utils'

/**
 * Analytics Service
 * Singleton that can be imported and called from any file
 */
class AnalyticsService {
  private adapter: AnalyticsAdapter | null = null
  private sessionId: string = getOrCreateSessionId()
  private consentGranted: boolean = false
  private config: AnalyticsConfig | null = null
  private isInitialized: boolean = false
  private userId: string | null = null

  /**
   * Initialize the analytics service
   * Call once at app startup before React renders
   */
  initialize(adapter: AnalyticsAdapter, config: AnalyticsConfig): void {
    this.adapter = adapter
    this.config = config
    this.isInitialized = true

    // Initialize adapter with first adapter config
    if (config.adapters.length > 0) {
      adapter.initialize(config.adapters[0]).catch(console.error)
    }

    if (config.debug) {
      console.log('[Analytics] Service initialized', {
        appId: config.appId,
        version: config.appVersion,
        environment: config.environment,
      })
    }
  }

  /**
   * Set consent state
   * Called when user grants/revokes consent
   */
  setConsent(granted: boolean): void {
    this.consentGranted = granted
    this.adapter?.setConsent(granted)

    if (this.config?.debug) {
      console.log('[Analytics] Consent updated:', granted)
    }
  }

  /**
   * Track a page view
   */
  trackPageView(event: Partial<PageViewEvent>): void {
    if (!this.shouldTrack()) return

    const fullEvent: PageViewEvent = {
      ...this.buildBaseProperties(),
      type: 'page_view',
      title: event.title || document.title,
      pagePath: event.pagePath || window.location.pathname,
      routeName: event.routeName,
      routeParams: event.routeParams,
      referrer: event.referrer || document.referrer || undefined,
      previousPageDuration: event.previousPageDuration,
    }

    this.adapter?.trackPageView(fullEvent)
  }

  /**
   * Track a custom event
   */
  trackEvent(payload: TrackEventPayload): void {
    if (!this.shouldTrack()) return

    const event: TrackEvent = {
      ...this.buildBaseProperties(),
      type: 'track',
      eventName: payload.eventName,
      category: payload.category,
      action: payload.action,
      label: payload.label,
      value: payload.value,
      properties: payload.properties
        ? sanitizePii(payload.properties, this.config?.piiFields)
        : undefined,
    }

    this.adapter?.track(event)
  }

  /**
   * Track an error
   * Errors are always tracked, even without consent
   */
  trackError(payload: ErrorPayload): void {
    if (!this.isInitialized || !this.adapter) return

    // Normalize error to an Error object
    let error: Error
    if (typeof payload.error === 'string') {
      error = new Error(payload.error)
    } else if (payload.error instanceof Error) {
      error = payload.error
    } else {
      // Handle cases where error is undefined, null, or an unknown type
      error = new Error(payload.error ? String(payload.error) : 'Unknown error')
    }

    const event: ErrorEvent = {
      ...this.buildBaseProperties(),
      type: 'error',
      errorName: error.name,
      errorMessage: error.message,
      stackTrace: error.stack?.substring(0, 2000),
      severity: payload.severity || 'medium',
      handled: payload.handled ?? true,
      componentName: payload.componentName,
      fingerprint: generateErrorFingerprint(error),
      context: payload.context
        ? sanitizePii(payload.context, this.config?.piiFields)
        : undefined,
    }

    this.adapter.trackError(event)
  }

  /**
   * Track a performance metric
   */
  trackPerformance(payload: PerformancePayload): void {
    if (!this.shouldTrack()) return

    const event: PerformanceEvent = {
      ...this.buildBaseProperties(),
      type: 'performance',
      metricName: payload.metricName,
      value: payload.value,
      rating: payload.rating,
      navigationType: payload.navigationType,
      attribution: payload.attribution,
    }

    this.adapter?.trackPerformance(event)
  }

  /**
   * Identify the current user
   */
  identify(userId: string, traits?: UserTraits): void {
    if (!this.shouldTrack()) return

    this.userId = userId

    const event: IdentifyEvent = {
      ...this.buildBaseProperties(),
      type: 'identify',
      userId,
      traits: traits ? sanitizePii(traits, this.config?.piiFields) : {},
    }

    this.adapter?.identify(event)
  }

  /**
   * Reset identity (call on logout)
   */
  reset(): void {
    this.userId = null
    this.sessionId = getOrCreateSessionId()
    clearSession()
    this.adapter?.reset()

    if (this.config?.debug) {
      console.log('[Analytics] Identity reset')
    }
  }

  /**
   * Flush pending events
   */
  async flush(): Promise<void> {
    await this.adapter?.flush()
  }

  /**
   * Get current session ID
   */
  getSessionId(): string {
    return this.sessionId
  }

  /**
   * Get current user ID
   */
  getUserId(): string | null {
    return this.userId
  }

  /**
   * Check if analytics is enabled and ready
   */
  isEnabled(): boolean {
    return this.isInitialized && (this.adapter?.isReady() ?? false)
  }

  /**
   * Check if tracking should proceed
   */
  private shouldTrack(): boolean {
    if (!this.isInitialized || !this.adapter) return false
    if (this.config?.requireConsent && !this.consentGranted) return false
    if (this.config?.sampleRate !== undefined && this.config.sampleRate < 1) {
      if (Math.random() > this.config.sampleRate) return false
    }
    return true
  }

  /**
   * Build base event properties
   */
  private buildBaseProperties() {
    return {
      timestamp: new Date().toISOString(),
      sessionId: this.sessionId,
      pageUrl:
        typeof window !== 'undefined'
          ? sanitizePii(window.location.href)
          : '',
      pagePath:
        typeof window !== 'undefined' ? window.location.pathname : '',
      userAgent:
        typeof navigator !== 'undefined' ? navigator.userAgent : '',
      deviceType: getDeviceType(),
      locale: getLocale(),
      isRtl: isRtl(),
    }
  }
}

/**
 * Singleton analytics instance
 * Import and use from anywhere in the app
 */
export const analytics = new AnalyticsService()
